






































































































































































































































































import { defineComponent, computed, ref, PropType, useContext } from '@nuxtjs/composition-api';
import dayjs from 'dayjs';
import { uniq } from 'lodash-es';
import ProductDetailAnnotationSection from '~/components/pages/Product/Detail/ProductDetailAnnotationSection.vue';
import ProductDetailStoreInventoryModalBody from '~/components/pages/Product/Detail/ProductDetailStoreInventoryModalBody.vue';
import ProductCartButton from '~/components/pages/Product/ProductCartButton.vue';
import AppFavoriteIconButton from '~/components/partials/App/AppFavoriteIconButton.vue';
import AppIcon from '~/components/partials/App/AppIcon.vue';
import AppImage from '~/components/partials/App/AppImage.vue';
import AppLink from '~/components/partials/App/AppLink.vue';
import AppModal from '~/components/partials/App/AppModal.vue';
import AppPrice from '~/components/partials/App/AppPrice.vue';
import AppRectButton from '~/components/partials/App/AppRectButton.vue';
import AppReviewStars from '~/components/partials/App/AppReviewStars.vue';
import ProductLabel from '~/components/partials/Product/ProductLabel.vue';
import { useMatchMedia } from '~/composables/useMatchMedia';
import { usePropSync } from '~/composables/usePropSync';
import { useStore } from '~/composables/useStore';
import { useWOVNio } from '~/composables/wovn/useWOVNio';
import {
  FLEXIBLE_SET_ITEM_SECTION_ANCHOR_POINT,
  STOCK_SECTION_ANCHOR_POINT,
  STOCK_NOTICE_BORDERLINE_COUNT,
  PRODUCT_CART_BUTTON,
  PAYMENT_METHOD_ID,
  CART_BUTTON_SECTION_ANCHOR_POINT,
} from '~/constants';
import { isInsidePeriod, url } from '~/utils';
import { getLotteryInfoFromEC } from '~/utils/checkLotteryStatus';
import {
  formatKeyValueArray,
  getSpecialPricePromotion,
  getProductVariant,
  getProductPrice,
  getProductUnit,
  filterReviewRating,
  parseReviewMediumInfo,
} from '~/utils/modules/products';
import { ProductShowECResponseSchema } from '~/types/modules/product';
import { ProductCategoryId, ProductSetType, PromotionsName, DisplayLang, LotteryApplicationType } from '~/enums';

export default defineComponent({
  components: {
    AppIcon,
    AppImage,
    AppFavoriteIconButton,
    AppLink,
    AppModal,
    AppPrice,
    AppReviewStars,
    AppRectButton,
    ProductLabel,
    ProductCartButton,
    ProductDetailAnnotationSection,
    ProductDetailStoreInventoryModalBody,
  },
  props: {
    ecResponse: {
      type: Object as PropType<ProductShowECResponseSchema>,
      required: true,
    },
    isFavorite: {
      type: Boolean,
      required: true,
    },
    primeurDeliveryText: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { isMobile } = useMatchMedia();
    const store = useStore();
    const { i18n } = useContext();
    const { currentLang } = useWOVNio();
    const { syncedIsFavorite } = usePropSync(props, 'isFavorite');

    const productCategory = computed(() => props.ecResponse.categories[0]?.id || ProductCategoryId.WINE);
    const hasMultipleVintages = computed(() => props.ecResponse.product_variants.length > 1);
    // D1出荷対象フラグを取得
    const isD1Target = computed(() => props.ecResponse.product_variants[0].d1_icon_display_flag);

    const isShownStoreInventory = ref(false);
    const isFitContent = ref(true);

    /**
     * ↓商品名、生産者↓
     */
    const productName = computed(() => ({
      ja: props.ecResponse.langs.find(({ id }) => id === 'ja')?.name,
      en: props.ecResponse.langs.find(({ id }) => id === 'en')?.name,
    }));
    const producerName = computed(
      () =>
        props.ecResponse.master_settings.producer && {
          ja: props.ecResponse.master_settings.producer.name_kana,
          en: props.ecResponse.master_settings.producer.name,
        }
    );
    const isJapaneseMode = computed(() => currentLang.value.code === DisplayLang.JA);
    /**
     * ↑商品名、生産者↑
     */

    /**
     * ↓クチコミ周り↓
     */
    const reviewAverage = computed(() => filterReviewRating(props.ecResponse.review_settings?.rating_average));

    const reviewLink = computed(() => url('ITEM_DETAIL', { code: props.ecResponse.code }) + '#detail_Rtoaster');
    /**
     * ↑クチコミ周り↑
     */

    /**
     * ↓入荷情報周り↓
     */
    /** 入荷情報表示期間中フラグ */
    const isShownStockInfo = computed(() => {
      return isInsidePeriod(props.ecResponse.stock_info_display_from, props.ecResponse.stock_info_display_to);
    });
    /**
     * ↑入荷情報、３行キャッチ周り↑
     */

    /**
     * ↓各種ラベル↓
     */
    const saleLabels = computed(() =>
      uniq(
        props.ecResponse.product_variants
          .map((variant) => {
            // 特価が設定されていない子商品のセールラベルは除く
            if (!getSpecialPricePromotion(variant.price_settings)) {
              return null;
            }
            return variant.price_settings.sale_info;
          })
          .filter((label) => label)
      )
    );
    const sameProductPromotionLabels = computed(() =>
      uniq(
        props.ecResponse.product_variants.flatMap(
          (variant) => variant.single_product_promotions.map(({ name }) => name) || []
        )
      )
    );
    const multiProductPromotionLabels = computed(() =>
      uniq(
        props.ecResponse.product_variants.flatMap(
          (variant) => variant.multi_product_promotions.map(({ name }) => name) || []
        )
      )
    );
    const hasVISAPromotion = computed(() => {
      if (!store.state.auth.authenticated) return false;

      return (
        props.ecResponse.product_variants.some((variant) =>
          variant.price_settings.promotions.some(({ name }) => name === PromotionsName.VISA_HAPPY_WINE_PRICE)
        ) && store.getters['user/info/isVisaMember']
      );
    });
    const isLimitedStock = computed(() =>
      props.ecResponse.product_variants.some(
        ({ attributes }) => formatKeyValueArray(attributes)?.mark_limited_stock_flag === 'true'
      )
    );
    const isOnlineOnly = computed(() => props.ecResponse.is_online_only);
    const isOnlineCampaign = computed(() =>
      props.ecResponse.product_variants.some(
        ({ attributes }) => formatKeyValueArray(attributes)?.mark_limited_online_campaign_flag === 'true'
      )
    );

    const hasLabels = computed(
      () =>
        sameProductPromotionLabels.value.length > 0 ||
        multiProductPromotionLabels.value.length > 0 ||
        saleLabels.value.length > 0 ||
        hasVISAPromotion.value ||
        isLimitedStock.value ||
        isOnlineCampaign.value ||
        (isOnlineOnly.value && hasShopStockButton.value) ||
        (isD1Target.value && props.ecResponse.product_variants.length === 1)
    );
    /**
     * ↑各種ラベル↑
     */

    /**
     * ↓価格周り↓
     */
    /** 最安子商品（価格と残り在庫数の表示用） */
    const bestPricedVariant = computed(() => {
      // NOTE: 全子商品が非表示の場合はbest_priced_variant_id: nullだけど、その場合はそもそも404エラーなので、実質必須項目になる
      const bestPricedVariant = getProductVariant(
        Number(props.ecResponse.best_priced_variant_id),
        props.ecResponse.product_variants
      );
      return {
        price: bestPricedVariant ? getProductPrice(bestPricedVariant.price_settings) : null,
        stockCount: bestPricedVariant?.stock_count,
      };
    });
    const hasMultiPrices = computed(
      () =>
        uniq(
          props.ecResponse.product_variants.map(
            (variant) =>
              getSpecialPricePromotion(variant.price_settings)?.price_including_tax ??
              variant.price_settings.price_including_tax
          )
        ).length > 1
    );
    /** プリムール用の1本あたり単価 */
    const pricePerBottle = computed(() => {
      if (!props.primeurDeliveryText) return 0;
      const priceSetting = props.ecResponse.product_variants[0]?.price_settings
        ? getProductPrice(props.ecResponse.product_variants[0]?.price_settings)
        : null;
      const sizeAmount = props.ecResponse.size_amount?.toString();
      let bottleCount = 1;
      switch (sizeAmount) {
        case '2250':
          bottleCount = 3;
          break;
        case '4500':
          bottleCount = 6;
          break;
        case '9000':
          bottleCount = 12;
          break;
      }
      // 小数点以下は切り捨てで表示する
      return Math.floor((priceSetting?.price || 0) / bottleCount);
    });
    /**
     * ↑価格周り↑
     */

    /**
     * ↓ヴィンテージ、サイズ、評価誌↓
     */
    const vintageCode = computed(() => {
      if (hasMultipleVintages.value) return;
      return props.ecResponse.product_variants[0].vintage_code;
    });
    const size = computed(() =>
      props.ecResponse.size_amount ? `${props.ecResponse.size_amount}${props.ecResponse.size_unit || 'ml'}` : undefined
    );
    const reviewMediaInfo = computed(() => {
      if (hasMultipleVintages.value) return [];
      return parseReviewMediumInfo(props.ecResponse.product_variants[0].review_medium_info);
    });
    /**
     * ↑ヴィンテージ、サイズ、評価誌↑
     */

    /** 残り在庫数の表示フラグ */
    const isShownStockCount = computed(
      () =>
        // [ワイン、その他お酒、ギフトセット]が対象外、子商品数関係なく最安子商品が対象
        ![ProductCategoryId.WINE, ProductCategoryId.OTHER_ALCOHOL, ProductCategoryId.GIFT_SET].includes(
          productCategory.value
        ) &&
        bestPricedVariant.value.stockCount &&
        bestPricedVariant.value.stockCount <= STOCK_NOTICE_BORDERLINE_COUNT
    );

    /** 支払い方法ロゴの表示フラグ */
    const showPaymentMethodsLogos = computed(() => {
      const notAllowedIds = props.ecResponse.not_allowed_payment_methods.map((method) => method.id);
      // rakuten pay, amazon pay, paidyが一つでも指定不可の場合はロゴを非表示
      return ![PAYMENT_METHOD_ID.RAKUTEN_PAY, PAYMENT_METHOD_ID.AMAZON_PAY, PAYMENT_METHOD_ID.PAIDY].some((item) =>
        notAllowedIds.includes(item)
      );
    });

    /** 最短出荷日を取得 */
    const earliestShippingDate = computed(() =>
      props.ecResponse.earliest_shipping_date && dayjs(props.ecResponse.earliest_shipping_date).isValid()
        ? dayjs(props.ecResponse.earliest_shipping_date).tz('Asia/Tokyo').format('MMMMDo')
        : ''
    );

    /**
     * ↓カートボタン、在庫ボタンなどの制御↓
     */
    const isFlexibleSetItem = computed(() => {
      return (
        productCategory.value === ProductCategoryId.WINE_SET &&
        (props.ecResponse.set_type as ProductSetType) === ProductSetType.FLEXIBLE_SET
      );
    });

    const hasStock = computed(
      () => (props.ecResponse.product_variants?.filter((variant) => variant.stock_count) || []).length > 0
    );

    const isNowOnSale = computed(() => props.ecResponse.product_variants.some((variant) => variant.now_on_sale_flag));

    const hasStockSection = computed(() =>
      [ProductCategoryId.WINE, ProductCategoryId.OTHER_ALCOHOL, ProductCategoryId.GIFT_SET].includes(
        productCategory.value
      )
    );

    const hasShopStockButton = computed(() => {
      const formattedProductAttributes = formatKeyValueArray(props.ecResponse.attributes);
      return formattedProductAttributes?.shop_stock_display_flag === 'true';
    });

    const lottery = computed(() => {
      const lotteryInfo = props.ecResponse.product_variants[0].raffle_item?.raffle;

      return {
        ...getLotteryInfoFromEC(lotteryInfo),
        text: i18n.t('Item-03.button.withinLotteryPeriod.type2').toString(),
      };
    });
    /**
     * ↑カートボタン、在庫ボタンなどの制御↑
     */

    /** 出荷情報、特別価格注記の表示フラグ */
    const shouldDisplayAnnotation = computed(() => {
      return (
        (!isD1Target.value && props.ecResponse.product_variants.length === 1) || bestPricedVariant.value.price?.isRed
      );
    });

    /** モーダルの大きさをコンテンツに合わせるかの真理値の更新 */
    const changeIsFitContent = (value: boolean) => {
      isFitContent.value = value;
    };

    return {
      lottery,
      isShownStoreInventory,
      productName,
      producerName,
      reviewMediaInfo,
      reviewAverage,
      reviewLink,
      isShownStockInfo,
      earliestShippingDate,
      bestPricedVariant,
      vintageCode,
      size,
      saleLabels,
      sameProductPromotionLabels,
      multiProductPromotionLabels,
      hasVISAPromotion,
      hasShopStockButton,
      hasStock,
      hasLabels,
      isShownStockCount,
      getProductUnit,
      isFlexibleSetItem,
      hasMultipleVintages,
      hasMultiPrices,
      hasStockSection,
      isNowOnSale,
      isLimitedStock,
      isOnlineOnly,
      isOnlineCampaign,
      isJapaneseMode,
      CART_BUTTON_SECTION_ANCHOR_POINT,
      FLEXIBLE_SET_ITEM_SECTION_ANCHOR_POINT,
      STOCK_SECTION_ANCHOR_POINT,
      isFitContent,
      changeIsFitContent,
      url,
      PRODUCT_CART_BUTTON,
      syncedIsFavorite,
      AppLink,
      showPaymentMethodsLogos,
      isD1Target,
      LotteryApplicationType,
      isMobile,
      pricePerBottle,
      shouldDisplayAnnotation,
    };
  },
});
